<template>
  <div class="container">
    <h1 class="subs-title">{{ getTitle() }}</h1>
    <text-field v-model="form.name" :validator="$v.form.name" label="Subscription name*" />
    <div class="input-container">
      <datetime-field
        v-model="form.from"
        :validator="$v.form.from"
        style="width: 100%"
        label="From*"
        :min="new Date().toString()"
        :disabled="!isEmpty(initialValue) && !!initialValue.prepaidCouponUser.length"
        :error-messages="{
          minDate: 'Start date must be a future date',
          maxDate: 'Start date should be before the end of the event',
        }"
      />
      <datetime-field
        v-model="form.to"
        :validator="$v.form.to"
        style="width: 100%"
        label="To*"
        :min="form.from ? form.from.toString() : new Date().toString()"
        :disabled="!isEmpty(initialValue) && !!initialValue.prepaidCouponUser.length"
        :error-messages="{
          minDate: 'End date should not be before the start of the event',
        }"
      />
    </div>
    <div class="input-container">
      <text-field
        v-model.number="form.price"
        :validator="$v.form.price"
        type="number"
        step="0.01"
        label="Price (EUR)*"
      />
    </div>
    <cover-field
      v-if="!organization.imageUrls.subscriptionCoverUrl"
      v-model="form.imageUrl"
      :validator="$v.form.imageUrl"
    />
    <textarea-field
      v-model="form.description"
      :validator="$v.form.description"
      label="Description"
      height="13.75rem"
    />
    <h2 class="second-title">Exclude events</h2>
    <p class="description">You can exclude tickets for performances by events name.</p>
    <select-field-multiple
      v-model="form.excludedEventIds"
      label="Exclude events"
      :items="allEventsInRange"
      item-render="title"
      item-select="id"
      :disabled="!isEmpty(initialValue) && !!initialValue.prepaidCouponUser.length"
    />

    <div class="buttons-wrapper" style="margin-top: 58px">
      <main-button text="CANCEL" flat depressed @click="$emit('cancel')" />
      <div class="buttons">
        <main-button :text="buttonName" @click="handleCreate" />
      </div>
    </div>
  </div>
</template>

<script>
import { isEmpty, omit } from 'lodash'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { submitGuard } from '@/utils'
import moment from 'moment'

export default {
  name: 'create-or-edit-sport-subscription-plan',
  props: {
    organizationSlug: {
      type: String,
    },
    initialValue: {
      type: Object,
      default: () => {},
    },
    eventTemplates: {
      type: Array,
      default: () => [],
    },
    interests: {
      type: Array,
      default: () => [],
    },
    allEvents: {
      type: Array,
      default: () => [],
    },
    organization: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    const eventIds =
      this.initialValue.couponItems && this.initialValue.couponItems.length
        ? this.initialValue.couponItems[0].eventIds
        : []
    const excludedEventIds = !isEmpty(this.initialValue)
      ? this.filterEventsByRange(this.allEvents, this.initialValue.from, this.initialValue.to)
          .map(e => e.id)
          .filter(id => !eventIds.includes(id))
      : []

    return {
      form: {
        ...omit(this.initialValue, ['couponItems', 'currency']),
        excludedEventIds,
      },
      buttonName: 'Create plan',
      emitType: 'create',
      attendeeFields: {},
    }
  },
  computed: {
    allEventsInRange() {
      if (!this.form.from || !this.form.to) {
        return this.allEvents
      }

      return this.filterEventsByRange(this.allEvents, this.form.from, this.form.to)
    },
  },
  validations() {
    return {
      form: {
        name: { required },
        price: { required },
        from: { required },
        to: { required },
        imageUrl: { required: requiredIf(() => !this.organization.imageUrls.subscriptionCoverUrl) },
      },
    }
  },
  methods: {
    isEmpty,
    getTitle() {
      if (Object.keys(this.initialValue).length) {
        this.buttonName = 'Update'
        this.emitType = 'update'

        return 'Edit Subscription Plan'
      } else {
        return 'Create Subscription Plan'
      }
    },
    removeSubscriptionItem(externalId) {
      this.form.couponItems.splice(
        this.form.couponItems.findIndex(item => item.externalId === externalId),
        1,
      )
    },
    updateCouponItem(info) {
      this.form.couponItems = this.form.couponItems.map(ci =>
        ci.externalId === info.externalId ? info : ci,
      )
    },
    handleCreate() {
      submitGuard(this, () => this.$emit(this.emitType, this.form))
    },
    filterEventsByRange(events, from, to) {
      return events.filter(
        event => moment(from).isBefore(moment(event.from)) && moment(to).isAfter(moment(event.to)),
      )
    },
  },
}
</script>

<style scoped>
.container {
  padding: 0 !important;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  max-width: 43.75rem;
}
.subs-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}
.input-container {
  display: flex;
  justify-content: space-between;
  width: 100%;
  column-gap: 1.5rem;
}
.second-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  color: #ff4c00;
  margin-bottom: 0.75rem;
  margin-top: 3rem;
}
</style>
